import {
  Paper,
  Box,
  Button,
  Typography,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { v4 as uuidV4 } from "uuid";
import moment from "moment";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Helmet } from "react-helmet-async";
import API from "../Api/api";
import toast from "react-hot-toast";
import DenseTable from "../ui/DenseTable";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";

const columns = [
  { id: "date", label: "DATE", width: 120 },
  {
    id: "description",
    label: "DESCRIPTION",
    width: 550,
    cellClassName: (params) => {
      const cellValue = params.value;
      return cellValue === "BALANCE PER THEIR BOOKS" ||
        cellValue === "LESS | OUR OUTSTANDING CREDITS" ||
        cellValue === "LESS | THEIR OUTSTANDING CREDITS" ||
        cellValue === "ADD | OUR OUTSTANDING DEBITS" ||
        cellValue === "ADD | THEIR OUTSTANDING DEBITS" ||
        cellValue === "BALANCE PER OUR BOOKS"
        ? "bold-cell"
        : "";
    },
  },
  {
    id: "outstanding",
    label: "OUTSTANDING",
    width: 150,
    align: "center",
    valueFormatter: (params) => {
      const formattedNumber = params.value
        ? Number(params.value).toLocaleString()
        : "";
      return formattedNumber;
    },
  },
  {
    id: "balance",
    label: "BALANCE",
    align: "center",
    width: 150,
    valueFormatter: (params) => {
      const formattedNumber = params.value
        ? Number(params.value).toLocaleString()
        : "";
      return formattedNumber;
    },
  },
  {
    id: "aging",
    label: "AGING",
    headerClassName: "center-header",
    align: "center",
    cellClassName: (params) => {
      const value = params.value;
      return value > 30 ? "red-cell" : "";
    },
    width: 260,
  },
];

export default function Report() {
  const locations = useSelector((state) => state.location) || [];
  const [reconciliationType, setReconciliationType] = React.useState("double");
  const [primary, setPrimary] = React.useState("head office");
  const [secondary, setSecondary] = React.useState("");
  const [singleLocation, setSingleLocation] = React.useState("");
  const [years, setYears] = React.useState([]);

  const [branchOutCr, setBranchOutCr] = React.useState([]);
  const [branchOutDr, setBranchOutDr] = React.useState([]);
  const [headOfficeOutCr, setHeadOfficeOutCr] = React.useState([]);
  const [headOfficeOutDr, setHeadOfficeOutDr] = React.useState([]);
  const [singleOutCr, setSingleOutCr] = React.useState([]);
  const [singleOutDr, setSingleOutDr] = React.useState([]);
  const [inputValue, setInputValue] = React.useState("");
  const [trigger, setTrigger] = React.useState(false);
  const [balance, setBalance] = React.useState([]);

  React.useEffect(() => {
    const branch =
      reconciliationType === "single"
        ? singleLocation
        : primary && secondary
          ? primary + " " + secondary
          : "";

    if (!branch) return;

    const fetch = async () => {
      let fetchingId;
      try {
        fetchingId = toast.loading("Fetching available dates....");
        const res = await API.get(
          `/api/v1/reconciliation-submission?branch=${branch}&approved=true`,
        );

        if (res.data) {
          setYears(res.data);
          toast.remove(fetchingId);
          toast.success("Dates Loaded");
        } else {
          setYears([]);
          setInputValue("");
          toast.remove(fetchingId);
        }
      } catch (error) {
        toast.remove(fetchingId);
        toast.error(error.message);
      }
    };

    fetch();
  }, [reconciliationType, primary, secondary, singleLocation]);

  const fetchData = async () => {
    let fetchingId;
    try {
      if (reconciliationType === "single") {
        if (!singleLocation || !inputValue) {
          setTrigger(false);
          toast.error("Please fill in required fields");
          return;
        }

        fetchingId = toast.loading("Generating report.....");

        const fetchSingleOutCr = await API.get(
          `/api/v1/outstanding-credit?location=primary&branch=${singleLocation}&submission_date=${inputValue}`,
        );

        const fetchSingleOutDr = await API.get(
          `/api/v1/outstanding-debit?location=primary&branch=${singleLocation}&submission_date=${inputValue}`,
        );

        const balance = await API.get(
          `/api/v1/reconciliation-submission?branch=${singleLocation}&submission_date=${inputValue}`,
        );

        setBalance(balance.data);
        setSingleOutCr(fetchSingleOutCr.data);
        setSingleOutDr(fetchSingleOutDr.data);
        setTrigger(true);

        toast.remove(fetchingId);
        toast.success("Report successfully generated");
        return;
      }

      if (primary && secondary && inputValue) {
        fetchingId = toast.loading("Generating report.....");

        const fetchBranchOutCr = await API.get(
          `/api/v1/outstanding-credit?location=secondary&branch=${
            secondary + " " + primary
          }&submission_date=${inputValue}`,
        );

        const fetchBranchOutDr = await API.get(
          `/api/v1/outstanding-debit?location=secondary&branch=${
            secondary + " " + primary
          }&submission_date=${inputValue}`,
        );

        const balance = await API.get(
          `/api/v1/reconciliation-submission?branch=${
            primary + " " + secondary
          }&submission_date=${inputValue}`,
        );

        setBalance(balance.data);

        const fetchHeadOfficeOutCr = await API.get(
          `/api/v1/outstanding-credit?location=primary&branch=${
            primary + " " + secondary
          }&submission_date=${inputValue}`,
        );

        const fetchHeadOfficeOutDr = await API.get(
          `/api/v1/outstanding-debit?location=primary&branch=${
            primary + " " + secondary
          }&submission_date=${inputValue}`,
        );

        setBranchOutCr(fetchBranchOutCr.data);
        setBranchOutDr(fetchBranchOutDr.data);
        setHeadOfficeOutCr(fetchHeadOfficeOutCr.data);
        setHeadOfficeOutDr(fetchHeadOfficeOutDr.data);
        setTrigger(true);

        toast.remove(fetchingId);
        toast.success("Report successfully generated");
      } else {
        setTrigger(false);
        toast.error("Please fill in required fields");
      }
    } catch (error) {
      toast.remove(fetchingId);
      toast.error(error.message);
    }
  };

  const handleChangePrimary = (event) => {
    setPrimary(event.target.value);
  };

  const handleChangeSecondary = (event) => {
    setSecondary(event.target.value);
  };

  const handleChangeSingleLocation = (event) => {
    setSingleLocation(event.target.value);
  };

  const handleChangeReconciliationType = (event, newType) => {
    if (!newType) return;
    setReconciliationType(newType);
    setTrigger(false);
    setInputValue("");
    setYears([]);
  };

  function createData(id, date1, description, outstanding, submission_date) {
    let aging;
    const date = moment(date1).format("DD-MM-YYYY");

    if (date1 && submission_date) {
      const date_ = moment(date1);
      const date2 = moment(submission_date);
      aging = date2.diff(date_, "days");
    }

    return {
      id,
      date,
      description,
      outstanding,
      aging,
      submission_date,
    };
  }

  const branchOutCrRow = branchOutCr.map((d) =>
    createData(
      d._id,
      d.value_date,
      d.description,
      d.credit_amt,
      d.submission_date,
    ),
  );

  const headOfficeOutCrRow = headOfficeOutCr.map((d) =>
    createData(
      d._id,
      d.value_date,
      d.description,
      d.credit_amt,
      d.submission_date,
    ),
  );

  const branchOutDrRow = branchOutDr.map((d) =>
    createData(
      d._id,
      d.value_date,
      d.description,
      d.debit_amt,
      d.submission_date,
    ),
  );

  const headOfficeOutDrRow = headOfficeOutDr.map((d) =>
    createData(
      d._id,
      d.value_date,
      d.description,
      d.debit_amt,
      d.submission_date,
    ),
  );

  const accountType = [];
  const branchBalance = [];
  const headOfficeBalance = [];
  const ourOutStandingCredit = [];
  const theirOutStandingCredit = [];
  const ourOutStandingDebit = [];
  const theirOutStandingDebit = [];

  branchOutCr.forEach((d) => {
    accountType.push(d.account_type);
    branchBalance.push(d.balance);
    theirOutStandingCredit.push(d.credit_amt);
  });

  branchOutDr.forEach((d) => {
    accountType.push(d.account_type);
    branchBalance.push(d.balance);
    theirOutStandingDebit.push(d.debit_amt);
  });

  headOfficeOutDr.forEach((d) => {
    accountType.push(d.account_type);
    headOfficeBalance.push(d.balance);
    ourOutStandingDebit.push(d.debit_amt);
  });

  headOfficeOutCr.forEach((d) => {
    accountType.push(d.account_type);
    headOfficeBalance.push(d.balance);
    ourOutStandingCredit.push(d.credit_amt);
  });

  const ourOutStandingCreditTotal = ourOutStandingCredit.reduce(
    (acc, val) => acc + val,
    0,
  );

  const theirOutStandingCreditTotal = theirOutStandingCredit.reduce(
    (acc, val) => acc + val,
    0,
  );

  const ourOutStandingDebitTotal = ourOutStandingDebit.reduce(
    (acc, val) => acc + val,
    0,
  );

  const theirOutStandingDebitTotal = theirOutStandingDebit.reduce(
    (acc, val) => acc + val,
    0,
  );

  const rows = [
    {
      id: uuidV4(),
      date: "",
      description: "SEKYERE RURAL BANK PLC, SEKYERE",
    },
    {
      id: uuidV4(),
      date: "",
      description: `RECONCILIATION OF ${secondary.toUpperCase()} BRANCH WITH ${primary.toUpperCase()} AS AT ${inputValue}`,
    },
    { id: uuidV4(), date: "", description: "" },
    {
      id: uuidV4(),
      date: "",
      description: "BALANCE PER BRANCH STATEMENT",
      balance: balance[0] ? balance[0].secondary_balance : 0,
    },
    { id: uuidV4(), date: "", description: "" },
    {
      id: uuidV4(),
      date: "",
      description: `${
        accountType[0] === "credit account" ? "LESS" : "ADD"
      } | HEAD OFFICE OUTSTANDING CREDITS`,
    },
    ...headOfficeOutCrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: ourOutStandingCreditTotal,
    },
    {
      id: uuidV4(),
      date: "",
      description: `${
        accountType[0] === "credit account" ? "LESS" : "ADD"
      } | BRANCH OUTSTANDING CREDITS`,
    },
    ...branchOutCrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: theirOutStandingCreditTotal,
    },
    {
      id: uuidV4(),
      date: "",
      description: `${
        accountType[0] === "credit account" ? "ADD" : "LESS"
      } | HEAD OFFICE OUTSTANDING DEBITS`,
    },
    ...headOfficeOutDrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: ourOutStandingDebitTotal,
    },
    {
      id: uuidV4(),
      date: "",
      description: `${
        accountType[0] === "credit account" ? "ADD" : "LESS"
      } | BRANCH OUTSTANDING DEBITS`,
    },
    ...branchOutDrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: theirOutStandingDebitTotal,
    },
    {
      id: uuidV4(),
      date: "",
      description: "BALANCE PER HEAD OFFICE STATEMENT",
      balance: balance[0] ? balance[0].primary_balance : 0,
    },
    {
      id: uuidV4(),
      date: "",
      description: `PREPARED BY: ${balance[0] ? balance[0].prepared_by : ""}`,
      aging: `APPROVED BY: ${balance[0] ? balance[0].approved_by : ""}`,
    },
  ];

  const singleOutCrRow = singleOutCr.map((d) =>
    createData(
      d._id,
      d.value_date,
      d.description,
      d.credit_amt,
      d.submission_date,
    ),
  );

  const singleOutDrRow = singleOutDr.map((d) =>
    createData(
      d._id,
      d.value_date,
      d.description,
      d.debit_amt,
      d.submission_date,
    ),
  );

  const singleOutCreditTotal = singleOutCr.reduce(
    (acc, val) => acc + val.credit_amt,
    0,
  );

  const singleOutDebitTotal = singleOutDr.reduce(
    (acc, val) => acc + val.debit_amt,
    0,
  );

  const singleRows = [
    {
      id: uuidV4(),
      date: "",
      description: "SEKYERE RURAL BANK PLC, SEKYERE",
    },
    {
      id: uuidV4(),
      date: "",
      description: `SINGLE ACCOUNT RECONCILIATION FOR ${singleLocation.toUpperCase()} AS AT ${inputValue}`,
    },
    { id: uuidV4(), date: "", description: "" },
    {
      id: uuidV4(),
      date: "",
      description: "BALANCE AT PERIOD START",
      balance: balance[0] ? balance[0].secondary_balance : 0,
    },
    { id: uuidV4(), date: "", description: "" },
    { id: uuidV4(), date: "", description: "ADD | OUTSTANDING CREDITS" },
    ...singleOutCrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: singleOutCreditTotal,
    },
    { id: uuidV4(), date: "", description: "LESS | OUTSTANDING DEBITS" },
    ...singleOutDrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: singleOutDebitTotal,
    },
    {
      id: uuidV4(),
      date: "",
      description: "COMPUTED BALANCE AT PERIOD END",
      balance: balance[0] ? balance[0].primary_balance : 0,
    },
    {
      id: uuidV4(),
      date: "",
      description: `PREPARED BY: ${balance[0] ? balance[0].prepared_by : ""}`,
      aging: `APPROVED BY: ${balance[0] ? balance[0].approved_by : ""}`,
    },
  ];

  function analyData(label) {
    return { label };
  }

  const dates = years
    .map((data) => analyData(moment(data.submission_date).format("YYYY-MM-DD")))
    .reverse();

  return (
    <Box>
      <Helmet>
        <title>Report | Reconciliation</title>
      </Helmet>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: "14px",
          border: "1px solid #e4e9ee",
          background: "#ffffff",
          mb: 1.5,
        }}
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          useFlexGap
          spacing={2}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={reconciliationType}
            onChange={handleChangeReconciliationType}
            sx={{
              alignSelf: "flex-start",
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px !important",
                px: 2,
              },
            }}
          >
            <ToggleButton value="double">Two Statements</ToggleButton>
            <ToggleButton value="single">Single Statement</ToggleButton>
          </ToggleButtonGroup>

          <Grid
            container
            spacing={2}
            alignItems="center"
            sx={{ flex: 1, minWidth: { xs: "100%", md: 600 }, mt: "-16px" }}
          >
            {reconciliationType === "single" ? (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel required>Location</InputLabel>
                  <Select
                    value={singleLocation}
                    label="Location"
                    onChange={handleChangeSingleLocation}
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {locations?.map((data) => (
                      <MenuItem key={data._id} value={data.name}>
                        {data.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel required>Primary Location</InputLabel>
                    <Select
                      value={primary}
                      label="Primary Location"
                      onChange={handleChangePrimary}
                      sx={{
                        borderRadius: "12px",
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {locations?.map((data) => (
                        <MenuItem key={data._id} value={data.name}>
                          {data.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel required>Secondary Location</InputLabel>
                    <Select
                      value={secondary}
                      label="Secondary Location"
                      onChange={handleChangeSecondary}
                      sx={{
                        borderRadius: "12px",
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {locations?.map((data) => (
                        <MenuItem key={data._id} value={data.name}>
                          {data.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={8} md={3}>
              <Autocomplete
                size="small"
                disablePortal
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                options={dates}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.label
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Available Dates"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "#f8fafc",
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchData}
                startIcon={<CompareArrowsOutlinedIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "12px",
                  py: 1.2,
                  background:
                    "linear-gradient(135deg, #21313c 0%, #344c5b 100%)",
                  boxShadow: "0 10px 18px rgba(33, 49, 60, 0.22)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #1a2730 0%, #2b404d 100%)",
                    boxShadow: "0 12px 20px rgba(33, 49, 60, 0.28)",
                  },
                }}
              >
                Generate Report
              </Button>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      {trigger ? (
        <Paper
          elevation={0}
          sx={{
            p: 1,
            borderRadius: "14px",
            border: "1px solid #e4e9ee",
            backgroundColor: "#ffffff",
            overflow: "hidden",
          }}
        >
          <DenseTable
            headers={columns}
            rows={reconciliationType === "single" ? singleRows : rows}
            filename={
              reconciliationType === "single"
                ? `${singleLocation}-${inputValue}`
                : `${primary}-${secondary}-${inputValue}`
            }
            height={"calc(100dvh - 265px)"}
          />
        </Paper>
      ) : (
        <Box>
          <Paper
            elevation={0}
            sx={{
              minHeight: "58vh",
              borderRadius: "22px",
              border: "1px dashed #cbd5df",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                maxWidth: 760,
              }}
            >
              <Box
                sx={{
                  width: 84,
                  height: 84,
                  mx: "auto",
                  mb: 2,
                  borderRadius: "24px",
                  background:
                    "linear-gradient(135deg, rgba(33,49,60,0.12) 0%, rgba(33,49,60,0.2) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AssessmentOutlinedIcon
                  sx={{ fontSize: 40, color: "#21313c" }}
                />
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "#21313c",
                  mb: 1,
                }}
              >
                Generate Reconciliation Report
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#667085",
                  lineHeight: 1.8,
                  fontSize: "1rem",
                }}
              >
                {reconciliationType === "single"
                  ? "Select a location and available reconciliation date to generate a complete report for review."
                  : "Select your primary location, secondary location, and available reconciliation date to generate a complete report for review."}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
